import first from 'lodash/first'
import isArray from 'lodash/isArray'
import reduce from 'lodash/reduce'
import React, { FC, useMemo } from 'react'

import { ImageProduct } from '@Components/CMS/ProductGrid'
import { ImageWithProductGridSlider } from '@Domains/Product/Features/ImageWithProductGridSlider/ImageWithProductGridSlider'
import { Block, ImageResource, Product } from '@Generated/graphql'
import {
  getJSONFieldValue,
  getLinkFieldValue,
  getLinkType,
  getPageReferences,
  getProductFieldValue,
  getProductReferenceValue,
  getReferenceFieldValue,
} from '@Globals/CMS/FieldHelpers'
import { Image } from '@Globals/Image'

const cmsProductToImageProduct = (product: Product): ImageProduct => ({
  id: product.id,
  link: product.url || '',
  title: product.name || '',
})

interface ProductAndImageGridBlockData {
  caption: string
  destPageType?: string
  destPageTypeID?: string
  displayValues?: string[]
  image: Image
  imageOnRight: boolean
  imageProducts: ImageProduct[]
  link?: string
  linkNewTab?: boolean
  productDisplay: 'slider' | 'grid-4' | 'grid-6' | 'v2'
  productIsCentered: boolean
  products: Product[]
}

const getBlockData = (productAndImageGridBlock: Block): ProductAndImageGridBlockData => {
  const imageResource = first(getReferenceFieldValue<ImageResource>(productAndImageGridBlock.fields || [], 'image'))
  const captionOptions = imageResource?.captionOptions
  const captionPageReferences = getPageReferences(captionOptions?.references || [])
  const products = getProductReferenceValue(productAndImageGridBlock.fields || [], 'products')
  const displayValues = getJSONFieldValue(productAndImageGridBlock.fields || [], 'display')

  const imageProducts = reduce(
    captionPageReferences,
    (memo, item) => {
      const product = getProductFieldValue(item.fields || [], 'product')
      if (!product) {
        return memo
      }
      return [...memo, cmsProductToImageProduct(product)]
    },
    [],
  )

  const link = getLinkFieldValue(imageResource?.fields || [], 'link')

  const image: Image = {
    dimensions: {
      height: imageResource?.image?.dimensions?.height || 0,
      width: imageResource?.image?.dimensions?.width || 0,
    },
    url: imageResource?.image?.url || '',
  }

  return {
    caption: captionOptions?.caption || '',
    destPageType: link ? getLinkType(link) : '',
    destPageTypeID: first(link?.references)?.id || '',
    displayValues: isArray(displayValues) ? displayValues as string[] : [],
    image,
    imageOnRight: getJSONFieldValue(productAndImageGridBlock.fields || [], 'imagePlacement') === 'Right',
    imageProducts,
    link: link?.url || '',
    linkNewTab: link?.newTab || false,
    productDisplay: getJSONFieldValue(productAndImageGridBlock.fields || [], 'productDisplay') || 'grid-4',
    productIsCentered: getJSONFieldValue(productAndImageGridBlock.fields || [], 'productAlignment') === 'Center',
    products,
  }
}

interface ProductAndImageGridBlockProps {
  id?: string
  isInModal?: boolean
  productAndImageGridBlock: Block
}

const ProductAndImageGridBlock: FC<ProductAndImageGridBlockProps> = ({ id, isInModal, productAndImageGridBlock }) => {
  const {
    caption,
    destPageType,
    destPageTypeID,
    displayValues,
    image,
    imageOnRight,
    link,
    linkNewTab,
    products,
  } = useMemo(() => getBlockData(productAndImageGridBlock), [productAndImageGridBlock])
  const showBuyButton = useMemo(() => !!displayValues?.some(displayValue => displayValue === 'buy-button'), [displayValues])

  return (
    <ImageWithProductGridSlider
      destPageType={destPageType}
      destPageTypeID={destPageTypeID}
      id={id}
      image={image}
      imageCaption={caption}
      imageOnRight={imageOnRight}
      isInModal={isInModal}
      link={link}
      linkNewTab={linkNewTab}
      products={products}
      showBuyButton={showBuyButton}
    />
  )
}

export default ProductAndImageGridBlock
